<template>
  <c-loading-area :loading="initialLoad" :height="350" :card="false">
    <transition name="scroll-x-reverse-transition" mode="out-in">
      <v-card v-if="hasSeenStep" key="table">
        <v-card-title>
          {{ $t('page.manageFiling.docketEntry.table.title') }}
        </v-card-title>
        <v-card-subtitle>
          {{ $t('page.manageFiling.docketEntry.table.description') }}
        </v-card-subtitle>

        <v-data-table
          :items="docketEntries"
          :headers="headers"
          :server-items-length="page.totalElements"
          :loading="loadingTable"
          class="px-4"
          item-key="resourceID"
          disable-sort
          disable-pagination
          hide-default-footer
          :mobile-breakpoint="800">
          <template #item.lead="{ value }">
            <template v-if="value">
              <v-icon>
                mdi-check
              </v-icon>
              <span class="d-sr-only">
                {{ $t('global.yes') }}
              </span>
            </template>
            <template v-else>
              <span class="d-sr-only">
                {{ $t('global.no') }}
              </span>
            </template>
          </template>
          <template v-if="offerExcludeFromService" #item.excludeFromService="{ value }">
            <template v-if="value">
              <v-icon>
                mdi-check
              </v-icon>
              <span class="d-sr-only">
                {{ $t('global.yes') }}
              </span>
            </template>
            <template v-else>
              <span class="d-sr-only">
                {{ $t('global.no') }}
              </span>
            </template>
          </template>
          <template #item.feeAmount="{ item }">
            <button type="button" class="link-button" @click="openFilingFeesPopup(item.resourceID)">
              {{ item.feeAmount | formatCurrency }}
            </button>
          </template>
          <template #item.documents="{ value }">
            <div v-for="d in value" :key="d.resourceID">
              <a :href="viewDocumentURL(d.documentLinkID)" target="_blank">
                {{ d.documentName }}
              </a>
            </div>
          </template>
          <template #item.resourceID="{ item }">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="primary" v-bind="attrs" v-on="on">
                  <v-icon>
                    mdi-dots-horizontal
                  </v-icon>
                  <span class="d-sr-only">
                    {{ $t('global.action') }}
                  </span>
                </v-btn>
              </template>
              <v-list dense style="min-width:100px">
                <v-list-item class="inset-focus" @click="$emit('edit', item.resourceID)">
                  <v-list-item-title>
                    {{ $t('global.edit') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item v-if="!item.lead" class="inset-focus" @click="remove(item.resourceID)">
                  <v-list-item-title>
                    {{ $t('global.remove') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <template #footer>
            <paging-controls :page="page" @changePage="changePage" read-only/>
          </template>
        </v-data-table>

        <v-fade-transition mode="out-in">
          <div v-if="loadingFeeWaiver" class="ma-4">
            <v-progress-linear indeterminate key="feeWaiverLoader"/>
          </div>
          <div v-else-if="hasFeeWaiver" class="mx-4 mt-4 mb-12" key="feeWaiverContent">
            <div class="c-caption">
              {{ $t('filing.feeWaiverRequest') }}
            </div>
            <div class="rounded pa-3" style="border:1px solid var(--line-color)">
              <div class="d-flex align-center" style="gap:16px">
                <div style="flex: 1 1 auto">
                  <div class="font-weight-medium">
                    {{ feeWaiver.displayName }}
                  </div>
                  <div class="text-minimum-gray" style="max-width:75ch">
                    {{ feeWaiver.description }}
                  </div>
                </div>
                <div style="flex: 0 0 auto">
                  <v-btn icon color="primary" @click="editFeeWaiver">
                    <span class="d-sr-only">
                      {{ $t('page.manageFiling.docketEntry.editFeeWaiver') }}
                    </span>
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon color="primary" @click="removeFeeWaiver">
                    <span class="d-sr-only">
                      {{ $t('page.manageFiling.docketEntry.removeFeeWaiver') }}
                    </span>
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </v-fade-transition>

        <v-card-actions class="card-actions-button-panel row-800 mt-7 pt-3">
          <div>
            <c-btn v-if="isStepComplete" type="primary" block @click="$emit('next')">
              {{ $t('global.continue') }}
            </c-btn>
          </div>
          <div>
            <c-btn block @click="$emit('startNew')">
              {{ $t('page.manageFiling.docketEntry.button.add') }}
            </c-btn>
          </div>
          <div v-if="showFeeWaiverButton">
            <c-btn block @click="openFeeWaiverPopup">
              {{ $t('page.manageFiling.docketEntry.requestFeeWaiver') }}
            </c-btn>
          </div>
        </v-card-actions>

        <request-fee-waiver-popup v-model="requestFeeWaiverPopupVisible"
                                  :editFeeWaiverID="editFeeWaiverID"
                                  @save="loadFeeWaiver"/>

        <filing-fees-popup v-model="filingFeesPopupVisible" :docketEntryID="filingFeesPopupDocketEntryID"/>
      </v-card>

      <docket-entries-splash-card v-else
                                  key="splash"
                                  :isLeadComplete="isLeadComplete"
                                  @startLead="$emit('startLead')"
                                  @viewDocketEntries="viewDocketEntries"/>

    </transition>
  </c-loading-area>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import PagingControls from '@components/support/PagingControls';
import DocketEntriesSplashCard from '@components/support/manageFiling/docketEntry/DocketEntriesSplashCard';
import RequestFeeWaiverPopup from '@components/support/manageFiling/docketEntry/RequestFeeWaiverPopup';
import FilingFeesPopup from '@components/support/manageFiling/FilingFeesPopup';
import Filing from '@/application/filing';
import { useFilingStore } from '@/stores/filingStore';

const filingStore = useFilingStore();

export default {
  components: { PagingControls, DocketEntriesSplashCard, RequestFeeWaiverPopup, FilingFeesPopup },
  data () {
    return {
      initialLoad: true,
      hasSeenStep: false,
      isLeadComplete: false,
      requestFeeWaiverPopupVisible: false,
      filingFeesPopupVisible: false,
      filingFeesPopupDocketEntryID: null,
      loadingTable: false,
      loadingFeeWaiver: false,
      editFeeWaiverID: null,
      docketEntries: [],
      feeWaiver: null,
      page: {
        size: 0,
        number: 0,
        totalElements: 0
      }
    };
  },
  computed: {
    headers () {
      let headers = [];
      headers.push({ text: this.$t('docketEntry.lead'), value: 'lead', sortable: false });
      headers.push({ text: this.$t('page.manageFiling.docketEntry.table.column.type'), value: 'docketEntryExternalName', sortable: false });
      headers.push({ text: this.$t('page.manageFiling.docketEntry.table.column.documents'), value: 'documents', sortable: false });

      if (this.offerExcludeFromService)
      {
        headers.push({ text: this.$t('page.manageFiling.docketEntry.table.column.excludeService'), value: 'excludeFromService',
          sortable: false, align: 'center' });
      }

      headers.push({ text: this.$t('page.manageFiling.docketEntry.table.column.filingFees'), value: 'feeAmount', sortable: false });
      headers.push({ text: this.$t('global.action'), value: 'resourceID', sortable: false, align: 'right' });

      return headers;
    },
    filingID () {
      return filingStore.filingID;
    },
    isStepComplete () {
      return filingStore.isStepComplete(this.CONST.FILING_STEP_DOCKET_ENTRIES);
    },
    offerExcludeFromService () {
      return filingStore.hasStep(this.CONST.FILING_STEP_SERVICE);
    },
    hasFeeWaiver () {
      return this.feeWaiver !== null;
    },
    feesGrandTotal () {
      let total = 0;
      this.docketEntries.forEach((de) => {
        total += _.defaultTo(de.feeAmount, 0);
      });

      return total;
    },
    showFeeWaiverButton () {
      return this.feesGrandTotal > 0 && !this.hasFeeWaiver;
    }
  },
  created () {
    this.hasSeenStep = filingStore.hasSeenStep;
    if (!this.hasSeenStep)
    {
      filingStore.markStepAsSeen();
    }

    Promise.all([this.loadDocketEntries(), this.loadFeeWaiver()])
      .then(() => {
        let leadDocuments = _.find(this.docketEntries, { lead: true }).documents;
        this.isLeadComplete = !_.isEmpty(leadDocuments);
      })
      .finally(() => {
        this.initialLoad = false;
      });
  },
  methods: {
    loadDocketEntries () {
      this.loadingTable = true;

      let url = this.$cove.getAPI({
        name: 'api.filing.docketEntries',
        query: {
          filingID: this.filingID,
          size: this.CONST.PAGING_MAX_SIZE,
          sort: ['lead,desc'],
          fields: '*,documents(*)'
        }
      });

      return axios.get(url).
        then((response) => {
          this.docketEntries = _.get(response, 'data._embedded.results', []);
          this.page.size = this.docketEntries.length;
          this.page.totalElements = this.docketEntries.length;
        })
        .finally(() => {
          this.loadingTable = false;
        });
    },
    loadFeeWaiver () {
      this.loadingFeeWaiver = true;

      let url = this.$cove.getAPI({
        name: 'api.filing',
        params: { id: this.filingID },
        query: { fields: '**,feeWaiverReason(*)' }
      });

      return axios.get(url).
        then((response) => {
          this.feeWaiver = _.get(response, 'data.feeWaiverReason', null);
        })
        .finally(() => {
          this.loadingFeeWaiver = false;
        });
    },
    changePage (newPage) {
      this.page.number = newPage;
      this.loadDocketEntries();
    },
    viewDocumentURL (documentLinkID) {
      return this.$cove.getAPI({ name: 'api.dms.document', params: { documentLinkID } });
    },
    viewDocketEntries () {
      this.hasSeenStep = true;
    },
    remove (docketEntryID) {
      this.$cove.confirm({ message: this.$t('application.remove.confirm') })
        .then((confirmed) => {
          if (confirmed)
          {
            this.$cove.blockNow();
            Filing.removeDocketEntry(docketEntryID)
              .then(() => {
                let p1 = this.loadDocketEntries();
                let p2 = filingStore.toggleSteps({ visible: true, refresh: true });
                return Promise.all([p1, p2]);
              })
              .then(() => {
                this.$cove.notify({ message: this.$t('application.remove.success'), color: 'success' });
              })
              .finally(() => {
                this.$cove.unblock();
              });
          }
        });
    },
    openFeeWaiverPopup () {
      this.editFeeWaiverID = null;
      this.requestFeeWaiverPopupVisible = true;
    },
    editFeeWaiver () {
      this.editFeeWaiverID = this.feeWaiver.resourceID;
      this.requestFeeWaiverPopupVisible = true;
    },
    removeFeeWaiver () {
      this.$cove.confirm({ message: this.$t('application.remove.confirm') })
        .then((confirmed) => {
          if (confirmed)
          {
            this.$cove.blockNow();

            let payload = {
              feeWaiverReasonID: null
            };

            Filing.patchFilingInfo({ filingID: this.filingID, payload })
              .then(() => {
                this.$cove.notify({ message: this.$t('application.remove.success'), color: 'success' });
                this.loadFeeWaiver();
              })
              .finally(() => {
                this.$cove.unblock();
              });
          }
        });
    },
    openFilingFeesPopup (docketEntryID) {
      this.filingFeesPopupVisible = true;
      this.filingFeesPopupDocketEntryID = docketEntryID;
    }
  }
};
</script>